/**
 * State container for Arrange-phase sessions.
 * 
 * Provides a minimal state container that wraps the Arrange engine in an object-oriented
 * interface. The engine itself remains stateless, while this class manages session state.
 */

import {
  type Item,
  type CriteriaSet,
  type ToolConfig,
  type Lens,
  type CanonicalScoreSet,
} from "../harmonizer";
import { runArrangeTool } from "./engine";

/**
 * Arrange-phase session state container.
 * 
 * Manages Items, CriteriaSet, ToolConfig, and Lens for a single Arrange-phase session.
 * Provides a convenient run() method that executes tools using the stored state.
 * 
 * This class is framework-agnostic and can be used in any context (React, Vue, vanilla JS, etc.).
 */
export class ArrangeSession {
  /**
   * Items to be prioritized in this session.
   */
  items: Item[];

  /**
   * CriteriaSet for evaluation (null if not required by tool).
   */
  criteriaSet: CriteriaSet | null;

  /**
   * Optional Lens for filtering/sorting items.
   */
  lens: Lens | null;

  /**
   * Tool-specific configuration.
   */
  toolConfig: ToolConfig;

  /**
   * Creates a new ArrangeSession with initial state.
   * 
   * @param items - Initial items (default: empty array)
   * @param criteriaSet - Initial CriteriaSet (default: null)
   * @param toolConfig - Initial ToolConfig (default: empty object)
   * @param lens - Initial Lens (default: null)
   */
  constructor(
    items: Item[] = [],
    criteriaSet: CriteriaSet | null = null,
    toolConfig: ToolConfig = {},
    lens: Lens | null = null
  ) {
    this.items = items;
    this.criteriaSet = criteriaSet;
    this.toolConfig = toolConfig;
    this.lens = lens;
  }

  /**
   * Runs a prioritization tool using the session's current state.
   * 
   * Uses the stored items, criteriaSet, toolConfig, and lens to execute the tool.
   * 
   * @param toolName - Name of the tool to run
   * @returns CanonicalScoreSet with tool execution results
   * @throws ArrangeEngineError if execution fails
   */
  async run(toolName: string): Promise<CanonicalScoreSet> {
    return runArrangeTool(
      toolName,
      this.items,
      this.criteriaSet,
      this.toolConfig,
      this.lens
    );
  }

  /**
   * Updates the session's items.
   * 
   * @param items - New items array
   */
  setItems(items: Item[]): void {
    this.items = items;
  }

  /**
   * Updates the session's CriteriaSet.
   * 
   * @param criteriaSet - New CriteriaSet (or null)
   */
  setCriteriaSet(criteriaSet: CriteriaSet | null): void {
    this.criteriaSet = criteriaSet;
  }

  /**
   * Updates the session's ToolConfig.
   * 
   * @param toolConfig - New ToolConfig object
   */
  setToolConfig(toolConfig: ToolConfig): void {
    this.toolConfig = toolConfig;
  }

  /**
   * Updates the session's Lens.
   * 
   * @param lens - New Lens (or null)
   */
  setLens(lens: Lens | null): void {
    this.lens = lens;
  }

  /**
   * Updates a specific field in the ToolConfig.
   * 
   * @param fieldName - Name of the field to update
   * @param value - New value for the field
   */
  setToolConfigField(fieldName: string, value: string | number | boolean | null): void {
    this.toolConfig = {
      ...this.toolConfig,
      [fieldName]: value,
    };
  }

  /**
   * Resets the session to initial state.
   * 
   * @param items - New items (optional, keeps current if not provided)
   * @param criteriaSet - New CriteriaSet (optional, keeps current if not provided)
   * @param toolConfig - New ToolConfig (optional, keeps current if not provided)
   * @param lens - New Lens (optional, keeps current if not provided)
   */
  reset(
    items?: Item[],
    criteriaSet?: CriteriaSet | null,
    toolConfig?: ToolConfig,
    lens?: Lens | null
  ): void {
    if (items !== undefined) {
      this.items = items;
    }
    if (criteriaSet !== undefined) {
      this.criteriaSet = criteriaSet;
    }
    if (toolConfig !== undefined) {
      this.toolConfig = toolConfig;
    }
    if (lens !== undefined) {
      this.lens = lens;
    }
  }
}


